import { ArrowRightIcon } from 'lucide-react'
import React from 'react'

import { useSubmitInNetworkVerificationRequest } from 'components/member_onboarding_v2/utils/useSubmitInNetworkVerificationRequest'
import { UnrecoverableErrorBanner } from 'components/members/components/UnrecoverableErrorBanner'
import { Button, ButtonGroup, Modal, useBreakpoint } from 'slab'

import { BottomSheetWithHeaderAndFooter } from '../BottomSheetWithHeaderAndFooter'
import { InNetworkPlansCard } from '../InNetworkPlansCard/InNetworkPlansCard'
import { InNetworkProviderCard } from '../InNetworkProviderCard/InNetworkProviderCard'
import { useProviderInNetworkVerificationContext } from '../ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'

export const ProviderInNetworkVerificationModal: React.FC<{
  onOpenChange: (open: boolean) => void;
  open: boolean;
  openProviderSearchModal?: () => void;
  onSelectMorePlansForVerification?: () => void;
}> = ({ open, onOpenChange, openProviderSearchModal, onSelectMorePlansForVerification }) => {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { canSubmit, selectedPlans, onRemovePlan, turnaroundDescription } = useProviderInNetworkVerificationContext()
  const description = `
    We share the most up-to-date data on whether your providers take a given insurance, but it can change pretty frequently.
    If you'd like, we can call your providers to double-check with them. ${turnaroundDescription}.
  `
  const { submitInNetworkVerificationRequest, isPending, error, confirmationModal } = useSubmitInNetworkVerificationRequest(() => onOpenChange(false))

  if (breakpointIsAtLeast('xs')) {
    return (
      <>
        {confirmationModal}
        <Modal open={open} onOpenChange={onOpenChange} width='large'>
          <div className='bg-gradient-to-t from-gray-50 to-thatch-100 overflow-y-auto'>
            <Modal.Body>
              <Modal.Title>
                Want us to call your providers?
              </Modal.Title>
              {!!error && <UnrecoverableErrorBanner />}
              <Modal.Description>{description}</Modal.Description>
              <InNetworkProviderCard openProviderSearchModal={openProviderSearchModal} />
              <InNetworkPlansCard
                plans={selectedPlans}
                onRemovePlan={onRemovePlan}
                onSelectMorePlansForVerification={onSelectMorePlansForVerification}
              />
            </Modal.Body>
          </div>
          <Modal.Footer>
            <div>
              <div className='caption text-gray-500 mb-3 text-center'>
                Note: we can only run this once. Double check you have the right plans and providers before submitting!
              </div>
              <ButtonGroup>
                <Modal.Button variant='secondary' disabled={isPending}>
                  Cancel
                </Modal.Button>
                <Modal.Button onClick={submitInNetworkVerificationRequest} variant='primary' rightIcon={ArrowRightIcon} disabled={isPending || !canSubmit}>
                  Submit
                </Modal.Button>
              </ButtonGroup>
            </div>
          </Modal.Footer>
        </Modal>
      </>
    )
  }
  return (
    <BottomSheetWithHeaderAndFooter
      open={open}
      onOpenChange={onOpenChange}
      title='Want us to call your providers?'
      footer={
        <div>
          <div className='caption text-gray-500 mb-3'>
            Note: we can only run this once. Double check you have the right plans and providers before submitting!
          </div>
          <ButtonGroup>
            <Button variant='tertiary-deemphasized' size='large' disabled={isPending || !canSubmit}>
              Cancel
            </Button>
            <Button
              size='large'
              onClick={submitInNetworkVerificationRequest}
              variant='primary'
              disabled={isPending}
            >
              Submit
            </Button>
          </ButtonGroup>
        </div>
      }
    >
      <div className='flex flex-col gap-4 pb-16'>
        {!!error && <UnrecoverableErrorBanner />}
        <div className='body-sm text-gray-800 mb-4'>{description}</div>
        <InNetworkProviderCard openProviderSearchModal={openProviderSearchModal} />
        <InNetworkPlansCard
          plans={selectedPlans}
          onRemovePlan={onRemovePlan}
          onSelectMorePlansForVerification={onSelectMorePlansForVerification}
        />
      </div>
    </BottomSheetWithHeaderAndFooter>
  )
}
